
import React, { useEffect, useState } from 'react';
import { Box, TableBody, IconButton, Tooltip } from '@mui/material';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  CreateButton
} from './styles';
import { useBreadcrumb } from '../../../context/BreadcrumbContext';
import { useSidebar } from '../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../store';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  getBankForms,
  softDeleteBankFormField as deleteBankFormField
} from '../../../modules/masterdata/actions';
import CreateBankFormModal from './components/CreateBankFormModal';
import EditBankFormModal from './components/EditBankFormModal';
import PreviewBankFieldModal from './components/PreviewBankFieldModal';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import {
  selectBankFormList,
  selectCreateBankFormFieldSuccess,
  selectCreateBankFormFieldError,
  selectUpdateBankFormFieldSuccess,
  selectUpdateBankFormFieldError,
  selectSoftDeleteBankFormFieldLoading as selectDeleteBankFormFieldLoading,
  selectSoftDeleteBankFormFieldSuccess as selectDeleteBankFormFieldSuccess,
  selectSoftDeleteBankFormFieldError as selectDeleteBankFormFieldError
} from '../../../modules/masterdata/selectors';
import {
  resetCreateBankFormFieldState,
  resetUpdateBankFormFieldState,
  resetSoftDeleteBankFormFieldState as resetDeleteBankFormFieldState
} from '../../../modules/masterdata/reducers';
import { IBankForm } from '../../../modules/masterdata/types';
import AlertPopup from '../../../components/common/AlertPopup';



const BankForm: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const forms = useSelector(selectBankFormList);

  const createSuccess = useSelector(selectCreateBankFormFieldSuccess);
  const createError = useSelector(selectCreateBankFormFieldError);

  const updateSuccess = useSelector(selectUpdateBankFormFieldSuccess);
  const updateError = useSelector(selectUpdateBankFormFieldError);

  const deleteLoading = useSelector(selectDeleteBankFormFieldLoading);
  const deleteSuccess = useSelector(selectDeleteBankFormFieldSuccess);
  const deleteError = useSelector(selectDeleteBankFormFieldError);

  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<IBankForm | null>(null);
  const [previewField, setPreviewField] = useState<IBankForm | null>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  useEffect(() => {
    setActivePage('bank-form');
    setPath([
      { title: 'Settings', path: '/master-data' },
      { title: 'Master Data', path: '/master-data' },
      { title: 'Bank Forms', path: '/master-data/bank-forms' },
    ]);
    setIsShow(true);
    dispatch(getBankForms());
  }, [setActivePage, setPath, setIsShow, dispatch]);

  // Create success/error effect
  useEffect(() => {
    if (createSuccess) {
      setAlert({ message: 'Field created successfully', severity: 'success' });
      dispatch(getBankForms());
      dispatch(resetCreateBankFormFieldState());
      setIsCreateModalOpen(false);
    }
    if (createError) {
      setAlert({ message: createError, severity: 'error' });
      dispatch(resetCreateBankFormFieldState());
    }
  }, [createSuccess, createError, dispatch]);

  // Update success/error effect
  useEffect(() => {
    if (updateSuccess) {
      setAlert({ message: 'Field updated successfully', severity: 'success' });
      dispatch(getBankForms());
      dispatch(resetUpdateBankFormFieldState());
      setIsEditModalOpen(false);
      setSelectedField(null);
    }
    if (updateError) {
      setAlert({ message: updateError, severity: 'error' });
      dispatch(resetUpdateBankFormFieldState());
    }
  }, [updateSuccess, updateError, dispatch]);

  // Delete success/error effect
  useEffect(() => {
    if (deleteSuccess) {
      setAlert({ message: 'Field deleted successfully', severity: 'success' });
      dispatch(getBankForms());
      dispatch(resetDeleteBankFormFieldState());
      setIsDeleteDialogOpen(false);
      setSelectedField(null);
    }
    if (deleteError) {
      setAlert({ message: deleteError, severity: 'error' });
      dispatch(resetDeleteBankFormFieldState());
    }
  }, [deleteSuccess, deleteError, dispatch]);

  const handlePreview = (field: IBankForm) => {
    setPreviewField(field);
    setIsPreviewOpen(true);
  };

  const handleEdit = (field: IBankForm) => {
    setSelectedField(field);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (field: IBankForm) => {
    setSelectedField(field);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (selectedField) {
      dispatch(deleteBankFormField(selectedField.field_id));
    }
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Manage Bank Forms</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={() => setIsCreateModalOpen(true)}>
            Add More Fields
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Field Name</StyledTableCell>
                <StyledTableCell>Field Type</StyledTableCell>
                <StyledTableCell>Field Slug</StyledTableCell>
                <StyledTableCell>Is Required</StyledTableCell>
                <StyledTableCell>Org</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {forms && forms.length > 0 ? (
                forms.map((form: IBankForm, index: number) => (
                  <StyledTableRow key={form.field_id}>
                    <StyledTableCell>{form.field_id}</StyledTableCell>
                    <StyledTableCell>{form.field_name}</StyledTableCell>
                    <StyledTableCell>{form.field_type}</StyledTableCell>
                    <StyledTableCell>{form.field_slug}</StyledTableCell>
                    <StyledTableCell>{form.is_required ? 'Yes' : 'No'}</StyledTableCell>
                    <StyledTableCell>{form.org ? form.org.org_name : 'Global'}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title="Preview Field">
                          <IconButton size="small" onClick={() => handlePreview(form)} sx={{ color: '#5E8E7E' }}>
                            <RemoveRedEyeIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {form.org && (
                          <>
                            <Tooltip title="Edit Field">
                              <IconButton size="small" color="primary" onClick={() => handleEdit(form)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Field">
                              <IconButton size="small" color="error" onClick={() => handleDeleteClick(form)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={7} align="center">No forms found.</StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </MainContainer>

      <CreateBankFormModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      />

      <EditBankFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        fieldData={selectedField}
      />

      <PreviewBankFieldModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        fieldData={previewField}
      />

      <ConfirmationDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Bank Form Field"
        message={`Are you sure you want to delete the field "${selectedField?.field_name}"?`}
        loading={deleteLoading}
      />
    </Box>
  );
};

export default BankForm;
